/**
 * Account-level configuration flags for the learner experience.
 *
 * Mirrors the `AccountConfig` shape returned by author-api
 * (`GET /api/scorm-accounts/:accountId/config`), which in turn sources these
 * from the hoot `accounts` table. Consumed by learner-facing surfaces (authoring
 * preview + exported player) through {@link AccountConfigProvider}.
 */
export interface AccountConfig {
  id: number;
  name: string;
  enableAskExpert: boolean;
  hideOrg: boolean;
  enforceFullVideoView: boolean;
  disableTracking: boolean;
  responseThreshold: number;
  /** Passive boundary duration in seconds; zero disables passive pacing. */
  contentPacingDuration: number;
  videoClosedCaption: boolean;
  displayPrivacyPolicyModal: boolean;
  selectedLanguages: string;
  /**
   * Account-level "Hide Results from Learners" (PLAT-999). When true, ungated
   * question results are suppressed for every applicable block regardless of the
   * block's own `hideResults` value.
   */
  hideResultsFromLearners: boolean;
}

/**
 * Neutral configuration used when no account is resolved (base/Emtrain view,
 * standalone player launch, loading, or fetch failure). All behavioral flags
 * are off; passive pacing retains its compatible 1.5-second default.
 */
export const DEFAULT_ACCOUNT_CONFIG: AccountConfig = {
  id: 0,
  name: '',
  enableAskExpert: false,
  hideOrg: false,
  enforceFullVideoView: false,
  disableTracking: false,
  responseThreshold: 0,
  contentPacingDuration: 1.5,
  videoClosedCaption: false,
  displayPrivacyPolicyModal: false,
  selectedLanguages: '',
  hideResultsFromLearners: false,
};

/**
 * Coerces a partial or untrusted config payload into a complete
 * {@link AccountConfig}, filling any missing or null field from
 * {@link DEFAULT_ACCOUNT_CONFIG}. The author-api payload can legitimately omit
 * fields (e.g. hoot returns no `selectedLanguages` for an account with no
 * language row) or carry null booleans straight from the DB, so consumers must
 * never index the raw payload directly — a missing `selectedLanguages` would
 * otherwise crash the language panel on `.split(',')`.
 */
type NullablePartial<T> = {
  [Key in keyof T]?: T[Key] | null;
};

export function normalizeAccountConfig(
  raw: NullablePartial<AccountConfig> | null | undefined,
): AccountConfig {
  const duration = raw?.contentPacingDuration;
  const contentPacingDuration = typeof duration === 'number'
    && Number.isFinite(duration)
    && duration >= 0
    && duration <= 3
    && Number.isInteger(duration * 2)
    ? duration
    : DEFAULT_ACCOUNT_CONFIG.contentPacingDuration;

  return {
    contentPacingDuration,
    id: raw?.id ?? DEFAULT_ACCOUNT_CONFIG.id,
    name: raw?.name ?? DEFAULT_ACCOUNT_CONFIG.name,
    enableAskExpert: raw?.enableAskExpert ?? DEFAULT_ACCOUNT_CONFIG.enableAskExpert,
    hideOrg: raw?.hideOrg ?? DEFAULT_ACCOUNT_CONFIG.hideOrg,
    enforceFullVideoView: raw?.enforceFullVideoView ?? DEFAULT_ACCOUNT_CONFIG.enforceFullVideoView,
    disableTracking: raw?.disableTracking ?? DEFAULT_ACCOUNT_CONFIG.disableTracking,
    responseThreshold: raw?.responseThreshold ?? DEFAULT_ACCOUNT_CONFIG.responseThreshold,
    videoClosedCaption: raw?.videoClosedCaption ?? DEFAULT_ACCOUNT_CONFIG.videoClosedCaption,
    displayPrivacyPolicyModal:
      raw?.displayPrivacyPolicyModal ?? DEFAULT_ACCOUNT_CONFIG.displayPrivacyPolicyModal,
    selectedLanguages: raw?.selectedLanguages ?? DEFAULT_ACCOUNT_CONFIG.selectedLanguages,
    hideResultsFromLearners:
      raw?.hideResultsFromLearners ?? DEFAULT_ACCOUNT_CONFIG.hideResultsFromLearners,
  };
}
